<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="640" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-form class="fdlg__form" @submit.prevent="save">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">Datele echipamentului</h2>
            <p class="fdlg__sub">{{ subtitle }}</p>
          </div>
          <v-btn icon aria-label="Închide" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <div class="item-edit__note">
            <v-icon small>mdi-information-outline</v-icon>
            <span>Se salvează în istoricul demersului («Date modificate»); demersul nu își schimbă etapa.</span>
          </div>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-desktop-classic</v-icon>Echipamentul</div>
            <v-row dense>
              <v-col cols="12">
                <v-combobox
                  v-model="model"
                  :search-input.sync="modelInput"
                  :items="suggestions"
                  label="Model*"
                  prepend-inner-icon="mdi-tag-outline"
                  outlined
                  hide-no-data
                  maxlength="150"
                  :loading="loadingOptions"
                  hint="Scrieți modelul; lista doar sugerează."
                  persistent-hint
                  :error-messages="errors.model"
                ></v-combobox>
              </v-col>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="registration"
                  label="Nr. inventar (opțional)"
                  prepend-inner-icon="mdi-barcode"
                  outlined
                  maxlength="150"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="7">
                <v-autocomplete
                  ref="location"
                  v-model="locationId"
                  :items="locationItems"
                  label="Locația*"
                  item-text="label"
                  item-value="id"
                  prepend-inner-icon="mdi-map-marker-outline"
                  outlined
                  :loading="loadingOptions"
                  :menu-props="locationMenuProps"
                  :error-messages="errors.location"
                  @focus="fitLocationMenu"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-message-text-outline</v-icon>Comentariu</div>
            <v-textarea
              v-model="comments"
              placeholder="Opțional: de ce modificați datele"
              outlined
              auto-grow
              rows="2"
              maxlength="2000"
              hide-details
              class="mb-3"
            ></v-textarea>
            <PhotoInput
              v-model="photos"
              purpose="comment"
              :max="6"
              label="Fotografii (opțional)"
              hint="Apar în drumul demersului. Nu fotografiați etichetele cu parole — acestea se pun la echipament, în «Fotografie etichetă»."
              @uploading="photosUploading = $event"
              @error="errorText = $event"
            />
          </section>

          <div v-if="errorText" class="form-error item-edit__error">{{ errorText }}</div>
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer></v-spacer>
          <v-btn text large @click="close">Anulare</v-btn>
          <v-btn class="btn-green" large type="submit" :loading="saving" :disabled="photosUploading">
            <v-icon left>mdi-content-save-outline</v-icon>Salvează
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import * as api from "@/views/approval/api";
import * as helper from "@/helper/helper";
import PhotoInput from "@/components/files/PhotoInput.vue";

const locationLabel = (loc) => [loc.block, loc.floor, loc.room].filter(Boolean).join(", ");

// Staff edit (admin, Șef DTI, ingineri) of an item that a request created: model, nr. inventar, room.
// `request` is a row of GET /requests/:id/requests-details.
export default {
  name: "ItemEditDialog",
  components: { PhotoInput },
  props: {
    request: { type: Object, default: null },
  },
  data() {
    return {
      dialog: false,
      saving: false,
      loadingOptions: false,
      suggestions: [],
      locations: [],
      model: "",
      modelInput: null,
      registration: "",
      locationId: null,
      comments: "",
      photos: [],
      photosUploading: false,
      errors: { model: "", location: "" },
      errorText: "",
      locationMenuWidth: null,
    };
  },
  computed: {
    subtitle() {
      if (!this.request) return "";
      return [this.request.equipment, this.request.cause].filter(Boolean).join(" · ");
    },
    // hidden rooms are not offered, but the item's current one stays visible
    locationItems() {
      const current = this.request && this.request.locationId;
      return this.locations
        .filter((loc) => !loc.is_hidden || loc.id === current)
        .map((loc) => ({ id: loc.id, label: locationLabel(loc) + (loc.is_hidden ? " (ascunsă)" : "") }))
        .sort((a, b) => a.label.localeCompare(b.label, "ro", { numeric: true }));
    },
    locationMenuProps() {
      return {
        offsetY: true,
        offsetOverflow: true,
        maxWidth: this.locationMenuWidth || "auto",
        contentClass: "menu-wrap",
      };
    },
  },
  methods: {
    open() {
      if (!this.request) return;
      this.model = this.request.model || "";
      this.modelInput = null;
      this.registration = this.request.registration_number || "";
      this.locationId = this.request.locationId || null;
      this.comments = "";
      this.photos = [];
      this.errors = { model: "", location: "" };
      this.errorText = "";
      this.dialog = true;
      helper.scrollToTop(this);
      this.loadOptions();
    },
    close() {
      if (this.saving) return;
      this.dialog = false;
    },
    async loadOptions() {
      this.loadingOptions = true;
      try {
        const [suggestions, locations] = await Promise.all([
          this.request.equipmentTypeId ? api.getModelSuggestions(this.request.equipmentTypeId) : Promise.resolve([]),
          this.locations.length ? Promise.resolve(this.locations) : api.getLocations(),
        ]);
        this.suggestions = suggestions || [];
        this.locations = locations || [];
      } catch (error) {
        console.error("Error loading item options:", error);
        this.errorText = error.response?.data?.message || "Lista de modele și locații nu a putut fi încărcată.";
      } finally {
        this.loadingOptions = false;
      }
    },
    fitLocationMenu() {
      this.locationMenuWidth = this.$refs.location.$el.offsetWidth;
    },
    // the combobox commits typed text only on blur; the save button must not lose it
    typedModel() {
      const typed = this.modelInput;
      const value = typed !== null && typed !== undefined ? typed : this.model;
      return String(value || "").trim();
    },
    // only what the person changed: the item may have been edited elsewhere since the request was filed
    changes() {
      const data = {};
      const model = this.typedModel();
      if (model !== (this.request.model || "")) data.model = model;
      const registration = this.registration.trim();
      if (registration !== (this.request.registration_number || "")) data.registration_number = registration;
      if (this.locationId && this.locationId !== this.request.locationId) data.location_id = this.locationId;
      return data;
    },
    async save() {
      if (this.saving || this.photosUploading) return;
      this.errorText = "";
      this.errors = {
        model: this.typedModel() ? "" : "Scrieți modelul echipamentului",
        location: this.locationId ? "" : "Alegeți locația",
      };
      if (this.errors.model || this.errors.location) return;

      const data = this.changes();
      const changed = Object.keys(data).length > 0;
      // photos alone are saved as a comment on the demer
      if (!changed && !this.photos.length) {
        this.errorText = "Nu ați modificat nimic.";
        return;
      }
      const note = this.comments.trim();
      if (note) data.comments = note;
      if (this.photos.length) data.file_ids = this.photos.map((photo) => photo.id);

      this.saving = true;
      try {
        await api.updateRequestItem(this.request.id, data);
        this.dialog = false;
        this.$emit("saved", changed ? "Datele echipamentului au fost salvate" : "Fotografiile au fost adăugate la demers");
      } catch (error) {
        console.error("Error saving item:", error);
        this.errorText = error.response?.data?.message || "Datele nu au fost salvate. Încercați din nou.";
      } finally {
        this.saving = false;
      }
    },
  },
  watch: {
    model() {
      this.errors.model = "";
    },
    modelInput() {
      this.errors.model = "";
    },
    locationId() {
      this.errors.location = "";
    },
  },
};
</script>

<style scoped>
.item-edit__note {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 16px;
  padding: 10px 14px;
  border-radius: 12px;
  background: var(--sky-bg);
  color: var(--sky);
  font-weight: 600;
}
.item-edit__note .v-icon {
  color: var(--sky);
  margin-top: 2px;
}
.item-edit__error {
  margin: 0 0 14px;
  font-weight: 600;
}
</style>
